import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { Node } from '@khc/contracts';
import { certificatesQueryOptions } from '../data/certs';
import { hubsQueryOptions } from '../data/queries';
import { formatRelativeTime } from '../i18n/format';
import { fmt, zh } from '../i18n/zh';
import { paths } from '../registry/routes';
import { PANEL_VERSION } from '../version';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Grid,
  Icon,
  PageHeader,
  Skeleton,
  Stack,
  Stat,
} from '../ui';

function statusBadge(status: Node['status']) {
  if (status === 'online') return <Badge tone="ok" label={zh.status.online} dot />;
  if (status === 'offline') return <Badge tone="danger" label={zh.status.offline} dot />;
  return <Badge tone="neutral" label={zh.status.unprobed} dot />;
}

export function OverviewPage() {
  const routerNavigate = useNavigate();
  const goTo = (to: string) => void routerNavigate({ to });

  // 请求预算：概览页只读控制面的两份列表（/hubs、/certificates）。
  // 不对每个节点 fan-out /apps、/mode、/state；运行态只在节点页或浏览器直连观测里看。
  const hubsQuery = useQuery(hubsQueryOptions());
  const certsQuery = useQuery(certificatesQueryOptions());
  const hubs: Node[] = hubsQuery.data ?? [];
  const certs = certsQuery.data ?? [];

  const onlineCount = hubs.filter((hub) => hub.status === 'online').length;
  const offlineHubs = hubs.filter((hub) => hub.status === 'offline');
  const unprobedCount = hubs.filter((hub) => hub.status !== 'online' && hub.status !== 'offline').length;

  const nowSec = Math.floor(Date.now() / 1000);
  const expiringCerts = certs.filter(
    (c) => c.notAfter !== null && c.notAfter - nowSec <= 86400 * 14,
  );
  const attentionCount = offlineHubs.length + expiringCerts.length;

  const summary =
    hubs.length === 0
      ? zh.overview.summaryEmpty
      : attentionCount > 0
        ? fmt(zh.overview.summaryAttention, { count: attentionCount })
        : zh.overview.summaryAllOk;

  return (
    <Stack gap={5}>
      <PageHeader
        title={zh.overview.title}
        summary={summary}
      >
        <p className="ui-panel-version">{fmt(zh.overview.panelVersion, { version: PANEL_VERSION })}</p>
      </PageHeader>

      {hubsQuery.isError ? (
        <Banner
          tone="danger"
          title={zh.hubs.loadError}
          action={
            <Button
              variant="default"
              size="sm"
              label={zh.common.retry}
              onClick={() => void hubsQuery.refetch()}
            />
          }
        />
      ) : null}

      {/* Top 4-Card Cluster KPI Strip */}
      <Grid cols={4} gap={3}>
        <Stat
          label={zh.overview.statHubs}
          value={`${onlineCount} / ${hubs.length}`}
          hint={
            offlineHubs.length > 0
              ? `${zh.overview.statOnline} ${onlineCount} · ${zh.overview.statOffline} ${offlineHubs.length}`
              : zh.overview.summaryAllOk
          }
        />
        <Stat
          label={zh.overview.statUnprobed}
          value={String(unprobedCount)}
          hint={zh.overview.statUnprobedHint}
        />
        <Stat
          label={zh.overview.statCerts}
          value={String(certs.length)}
          hint={
            expiringCerts.length > 0
              ? fmt(zh.overview.statCertsHint, { expiring: expiringCerts.length })
              : zh.overview.statCertsOk
          }
        />
        <Stat
          label={zh.overview.statAttention}
          value={String(attentionCount)}
          hint={zh.overview.statAttentionHint}
        />
      </Grid>

      {/* Compact Actionable Triage Strip (only shown when attention is needed) */}
      {attentionCount > 0 ? (
        <div className="ui-triage-strip" role="region" aria-label={zh.overview.attention}>
          <div className="ui-triage-strip-head">
            <Badge
              tone="warn"
              label={`${zh.overview.attention} · ${attentionCount}`}
            />
          </div>
          <div className="ui-triage-strip-items">
            {offlineHubs.map((hub) => (
              <button
                key={hub.id}
                type="button"
                className="ui-triage-chip"
                onClick={() => goTo(paths.hub(hub.id))}
              >
                <Badge tone="danger" label={zh.status.offline} dot />
                <span className="ui-triage-name">{hub.name}</span>
                <span className="ui-triage-desc">
                  {fmt(zh.overview.nodeOfflineReason, {
                    time: hub.lastSeenAt ? formatRelativeTime(hub.lastSeenAt) : zh.common.unknown,
                  })}
                </span>
                <Icon name="arrow" size={14} />
              </button>
            ))}
            {expiringCerts.map((cert) => {
              const daysLeft = Math.max(
                0,
                Math.ceil(((cert.notAfter ?? nowSec) - nowSec) / 86400),
              );
              return (
                <button
                  key={cert.id}
                  type="button"
                  className="ui-triage-chip"
                  onClick={() => goTo(`${paths.settings}?tab=certs`)}
                >
                  <Badge tone="warn" label={zh.nav.certs} dot />
                  <span className="ui-triage-name">{cert.name}</span>
                  <span className="ui-triage-desc">
                    {fmt(zh.overview.certExpiringReason, { days: daysLeft })}
                  </span>
                  <Icon name="arrow" size={14} />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {hubsQuery.isLoading ? (
        <Grid cols={2} gap={4}>
          <Skeleton height={210} />
          <Skeleton height={210} />
        </Grid>
      ) : hubs.length === 0 ? (
        <Card>
          <EmptyState
            icon="server"
            title={zh.overview.noHubs}
            description={zh.overview.noHubsHint}
            action={
              <Button
                variant="primary"
                icon="plus"
                label={zh.overview.addFirstHub}
                onClick={() => goTo(`${paths.hubs}?create=1`)}
              />
            }
          />
        </Card>
      ) : (
        <Card
          title={zh.overview.nodeMatrixTitle}
          action={
            <Button
              variant="ghost"
              size="sm"
              label={zh.overview.manageHubs}
              onClick={() => goTo(paths.hubs)}
            />
          }
        >
          <div className="ui-hub-grid">
            {hubs.map((hub) => {
              const toneClass =
                hub.status === 'online'
                  ? 'ui-hub-tile-ok'
                  : hub.status === 'offline'
                    ? 'ui-hub-tile-danger'
                    : '';

              return (
                <div
                  key={hub.id}
                  role="link"
                  tabIndex={0}
                  className={`ui-hub-tile ${toneClass}`.trim()}
                  onClick={() => goTo(paths.hub(hub.id))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goTo(paths.hub(hub.id));
                    }
                  }}
                >
                  <div className="ui-hub-tile-head">
                    <div className="ui-hub-tile-title-row">
                      <span className="ui-hub-tile-name">{hub.name}</span>
                    </div>
                    <div className="ui-Cluster">
                      {statusBadge(hub.status)}
                      <span className="sr-only">{zh.overview.hubCardEnter}</span>
                      <span className="ui-hub-tile-chevron" aria-hidden="true">
                        <Icon name="arrow" />
                      </span>
                    </div>
                  </div>

                  <div className="ui-hub-tile-endpoint-row">
                    <span className="ui-hub-tile-endpoint">{hub.endpoint}</span>
                    <span className="ui-hub-tile-route-chip">
                      {hub.preferredRoute === 'tunnel' ? zh.hubs.routeTunnel : zh.hubs.routeDirect}
                    </span>
                  </div>

                  <div className="ui-hub-offline-bar">
                    <span>
                      {zh.fields.lastSeen}：{hub.lastSeenAt ? formatRelativeTime(hub.lastSeenAt) : zh.common.dash}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </Stack>
  );
}

export default OverviewPage;
